import type { BoardDisplayState } from '../../lib/liveLessonTypes';
import { QRCodeSVG } from 'qrcode.react';

type BoardSceneRendererProps = {
  state: BoardDisplayState;
};

function renderTimer(state: BoardDisplayState) {
  if (!state.timer) return '00:00';
  const min = Math.floor(Math.max(0, state.timer.remainingSec) / 60);
  const sec = Math.max(0, state.timer.remainingSec) % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function BoardSceneRenderer({ state }: BoardSceneRendererProps) {
  if (!state.enabled) {
    return <div className="text-center text-2xl font-semibold text-gray-500">Board nicht aktiv</div>;
  }

  // Compatibility path: if quiz payload is present, render quiz regardless of scene.
  const quiz = state.quiz;
  if (quiz && ['lobby', 'question', 'reveal', 'finished'].includes(quiz.phase)) {
    const progressCurrent = Math.min((quiz.questionIndex || 0) + 1, Math.max(1, quiz.totalQuestions));
    const progressPercent = Math.max(0, Math.min(100, (progressCurrent / Math.max(1, quiz.totalQuestions)) * 100));

    if (quiz.phase === 'finished') {
      return (
        <div className="mx-auto w-full max-w-6xl rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50/50 p-10 shadow-2xl">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">Lernerfolgskontrolle</div>
              <div className="mt-3 text-5xl font-extrabold tracking-tight text-slate-900">Finale Rangliste</div>
              <div className="mt-2 text-2xl font-semibold text-slate-700">{quiz.topic}</div>
            </div>
            <div className="rounded-2xl bg-indigo-600 px-6 py-3 text-center text-white shadow-lg">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-100">Status</div>
              <div className="mt-1 text-lg font-bold">Quiz beendet</div>
            </div>
          </div>
          <div className="mt-8 space-y-3">
            {(quiz.leaderboard || []).slice(0, 10).map((entry, index) => (
              <div
                key={`${entry.name}-${index}`}
                className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-2xl shadow-sm ${
                  index === 0
                    ? 'border-amber-300 bg-amber-50'
                    : index === 1
                      ? 'border-slate-300 bg-slate-100'
                      : index === 2
                        ? 'border-orange-300 bg-orange-50'
                        : 'border-slate-200 bg-white/90'
                }`}
              >
                <span className="font-semibold text-slate-800">
                  {index + 1}. {entry.name}
                </span>
                <span className="rounded-lg bg-slate-900 px-3 py-1 font-bold text-white">{entry.score} P</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (quiz.phase === 'lobby') {
      const joinUrl =
        quiz.joinToken && typeof window !== 'undefined'
          ? `${window.location.origin}/student/quiz/${quiz.joinToken}`
          : null;
      return (
        <div className="mx-auto w-full max-w-6xl rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-indigo-50/30 to-cyan-50/40 p-10 shadow-2xl">
          <div className="grid items-center gap-10 md:grid-cols-[1.3fr_1fr]">
            <div>
              <div className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-100/70 px-4 py-1 text-sm font-semibold uppercase tracking-[0.18em] text-indigo-700">
                Quiz Lobby
              </div>
              <div className="mt-5 text-6xl font-extrabold leading-tight tracking-tight text-slate-900">{quiz.topic}</div>
              <div className="mt-5 text-2xl leading-relaxed text-slate-700">Scanne den QR-Code und melde dich auf deinem Geraet an.</div>
              <div className="mt-6 inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-lg font-semibold text-white">
                Bereit fuer Frage {progressCurrent}/{Math.max(1, quiz.totalQuestions)}
              </div>
            </div>
            {joinUrl ? (
              <div className="flex flex-col items-center rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
                <QRCodeSVG value={joinUrl} size={230} />
                <div className="mt-4 max-w-sm break-all text-center text-xs text-slate-500">{joinUrl}</div>
              </div>
            ) : (
              <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-slate-200 bg-white text-slate-500 shadow-lg">
                Join-Link wird vorbereitet...
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mx-auto w-full max-w-6xl rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50/30 p-10 shadow-2xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
            Frage {progressCurrent}/{Math.max(1, quiz.totalQuestions)}
          </div>
          <div className={`rounded-full px-4 py-1 text-sm font-semibold ${quiz.phase === 'reveal' ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>
            {quiz.phase === 'reveal' ? 'Aufloesung' : 'Antwortphase'}
          </div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-500" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="mt-6 whitespace-pre-wrap text-5xl font-extrabold leading-tight tracking-tight text-slate-900">
          {quiz.prompt || 'Quiz laeuft...'}
        </div>
        <div className="mt-9 grid gap-4 md:grid-cols-2">
          {(quiz.options || []).map((entry, index) => (
            <div
              key={`${entry}-${index}`}
              className={`rounded-2xl border px-5 py-4 text-2xl shadow-sm ${
                quiz.phase === 'reveal' && quiz.correctOptionLabel === entry
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  : 'border-slate-200 bg-white text-slate-800'
              }`}
            >
              <span className="mr-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-base font-bold text-white">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="align-middle">{entry}</span>
            </div>
          ))}
        </div>
        {quiz.phase === 'reveal' ? (
          <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-xl text-emerald-900 shadow-sm">
            <div className="text-sm font-semibold uppercase tracking-[0.16em] text-emerald-700">Richtige Antwort</div>
            <div className="mt-1 text-2xl font-bold">{quiz.correctOptionLabel || '-'}</div>
            {quiz.correctStudentNames?.length ? (
              <div className="mt-3 text-base text-emerald-800">
                Richtig hatten: <span className="font-semibold">{quiz.correctStudentNames.join(', ')}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (state.scene === 'blank') {
    return <div className="text-center text-3xl font-semibold text-gray-500">Bereit</div>;
  }

  if (state.scene === 'task') {
    return (
      <div className="mx-auto max-w-5xl rounded-2xl border border-gray-300 bg-white/95 p-10 text-center shadow">
        <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Aufgabe</div>
        <div className="whitespace-pre-wrap text-5xl font-bold text-gray-900">{state.taskText || 'Keine Aufgabe gesetzt'}</div>
      </div>
    );
  }

  if (state.scene === 'timer_only') {
    return <div className="text-center text-8xl font-bold text-gray-900">{renderTimer(state)}</div>;
  }

  if (state.scene === 'progress') {
    const done = state.progress?.completedPhaseIds.length || 0;
    const total = state.progress?.total || 0;
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-gray-300 bg-white/95 p-10 text-center">
        <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">Verlauf</div>
        <div className="mt-3 text-6xl font-bold text-gray-900">
          {done}/{total}
        </div>
      </div>
    );
  }

  if (state.scene === 'room_energy') {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-gray-300 bg-white/95 p-10 text-center">
        <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">Raumenergie</div>
        <div className="mt-4 text-6xl font-bold text-gray-900">{state.roomEnergy?.band || 'balanced'}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl rounded-2xl border border-gray-300 bg-white/95 p-10 text-center shadow">
      <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">{state.phaseLabel || 'Phase'}</div>
      <div className="mt-4 text-7xl font-bold text-gray-900">{renderTimer(state)}</div>
      <div className="mt-4 text-2xl text-gray-600">{state.workMode || ''}</div>
    </div>
  );
}
